// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Shared review navigation for the per-currency close steps (rollforwards and prior-period
 * adjustments). Sections start expanded; reviewing the last entry that needs review in a section
 * scrolls to the next currency, and asking to continue while entries remain expands the target
 * section (if collapsed), scrolls to the next entry, and flashes it. Each step supplies its own
 * layout and wires these values into PerCurrencySections, JournalEntriesTable, and the Continue
 * button.
 */
import {
  CloseRunCurrencyBucket,
  JournalEntryWithReviewState,
} from '@/accounting/api/types';
import { isPendingReview } from '@/accounting/utilities/is-pending-review';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { useEffect, useState } from 'react';

interface Params {
  entries: JournalEntryWithReviewState[];
  buckets: CloseRunCurrencyBucket[];
  onEntriesChange: (entries: JournalEntryWithReviewState[]) => void;
  // Namespaces the per-section DOM ids so concurrent steps never collide.
  idNamespace: string;
}

interface PerCurrencyReview {
  // Pass to PerCurrencySections so sections carry scroll ids.
  sectionIdPrefix: string;
  // Controlled expansion for PerCurrencySections.
  expandedCurrencyCodes: ReadonlySet<string>;
  onToggleSection: (currencyCode: IsoCurrencyCode) => void;
  // Entries in a currency still needing review; drives the section's status chip.
  sectionPendingCount: (currencyCode: IsoCurrencyCode) => number;
  // Wire into JournalEntriesTable.
  flashEntryId: string | null;
  onFlashComplete: (entryId: string) => void;
  handleMarkReviewed: (entryId: string) => void;
  // Wire into the Continue button.
  allReviewed: boolean;
  onContinueBlocked: () => void;
}

export function usePerCurrencyReview(params: Params): PerCurrencyReview {
  const { entries, buckets, onEntriesChange, idNamespace } = params;
  const [flashEntryId, setFlashEntryId] = useState<string | null>(null);
  const [pendingScrollElementId, setPendingScrollElementId] = useState<
    string | null
  >(null);
  // Seeded on first render rather than in an effect: the sections are controlled, so an empty
  // initial set painted every one collapsed and then animated them all open on each mount.
  const [expandedCurrencyCodes, setExpandedCurrencyCodes] = useState<
    Set<string>
  >(() => new Set(buckets.map((bucket) => bucket.currency)));

  // Scroll to the next section once auto-advance has expanded it.
  useEffect(() => {
    if (pendingScrollElementId === null) {
      return;
    }

    document
      .getElementById(pendingScrollElementId)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setPendingScrollElementId(null);
  }, [pendingScrollElementId]);

  const allReviewed = entries.every((entry) => !isPendingReview(entry));

  const onToggleSection = (currencyCode: IsoCurrencyCode) => {
    setExpandedCurrencyCodes((prev) => {
      const next = new Set(prev);
      if (next.has(currencyCode)) {
        next.delete(currencyCode);
      } else {
        next.add(currencyCode);
      }
      return next;
    });
  };

  const ensureExpanded = (currencyCode: IsoCurrencyCode) => {
    setExpandedCurrencyCodes((prev) => {
      if (prev.has(currencyCode)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(currencyCode);
      return next;
    });
  };

  const sectionPendingCount = (currencyCode: IsoCurrencyCode) =>
    entries.filter(
      (entry) => entry.currency === currencyCode && isPendingReview(entry)
    ).length;

  const handleMarkReviewed = (entryId: string) => {
    const updated = entries.map((entry) =>
      entry.id === entryId ? { ...entry, reviewed: true } : entry
    );
    onEntriesChange(updated);

    // When the reviewed entry's section has nothing left to review, advance to the next section.
    const reviewedEntry = updated.find((entry) => entry.id === entryId);
    if (!reviewedEntry) {
      return;
    }
    const sectionIso = reviewedEntry.currency;
    const sectionComplete = updated
      .filter((entry) => entry.currency === sectionIso)
      .every((entry) => !isPendingReview(entry));
    if (!sectionComplete) {
      return;
    }
    const currentIndex = buckets.findIndex(
      (bucket) => bucket.currency === sectionIso
    );
    const nextBucket = buckets.at(currentIndex + 1);
    if (nextBucket) {
      ensureExpanded(nextBucket.currency);
      setPendingScrollElementId(`${idNamespace}-${nextBucket.currency}`);
    }
  };

  // Point the user at the next entry that still needs review: expand its section and flash it.
  // The scroll is left to useFocusHighlight on the flashed row, which already scrolls only when
  // the row is off screen and measures after the section has expanded. Queueing a scroll here too
  // fired against the collapsing section's height-0 geometry and overrode that off-screen rule.
  const onContinueBlocked = () => {
    const nextEntry = entries.find(isPendingReview);
    if (nextEntry) {
      ensureExpanded(nextEntry.currency);
      setFlashEntryId(nextEntry.id);
    }
  };

  const onFlashComplete = (entryId: string) => {
    setFlashEntryId((current) => (current === entryId ? null : current));
  };

  return {
    sectionIdPrefix: idNamespace,
    expandedCurrencyCodes,
    onToggleSection,
    sectionPendingCount,
    flashEntryId,
    onFlashComplete,
    handleMarkReviewed,
    allReviewed,
    onContinueBlocked,
  };
}
